import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { CancelDialog } from "@/components/common/Modals"
import { SendDialogActions } from "@/screens/Pricing/components/dialogs/SendDialog/SendDialogActions"
import SendDialogTitle from "@/screens/Pricing/components/dialogs/SendDialog/SendDialogTitle"
import ViewRecipientAccordion from "@/screens/Pricing/components/dialogs/SendDialog/ViewRecipientAccordion"
import EmailTemplateForm from "@/screens/Pricing/components/forms/EmailTemplateForm"
import type { PriceChangeSubmitContext } from "@/screens/Pricing/components/PriceChangeTables"
import { DEFAULT_EMAIL_NOTE } from "@/screens/Pricing/constants/submitPriceChange"
import { useDeleteSubmission } from "@/screens/Pricing/hooks/api/useDeletePriceChangeSubmission"
import { usePromoteDraftSubmission } from "@/screens/Pricing/hooks/api/usePromoteDraftSubmission"
import { useGetPriceChangePreview } from "@/screens/Pricing/hooks/useGetPriceChangePreview"
import { useGetPriceChangeRecipients } from "@/screens/Pricing/hooks/useGetPriceChangeRecepients"
import type { SendPriceChangeSchemaFormValues } from "@/screens/Pricing/schema/SendPriceChangeSchema"
import { SendPriceChangeSchema } from "@/screens/Pricing/schema/SendPriceChangeSchema"
import { isNil } from "@/utils/utils"

interface SendPricingDialogProps {
  onClose: () => void
  onSuccess: () => void
  submitContext: PriceChangeSubmitContext
  submissionId: string
}

const SendPriceChange: FC<SendPricingDialogProps> = ({ onClose, onSuccess, submitContext, submissionId }) => {
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { promoteDraftSubmission, isPromoting } = usePromoteDraftSubmission()
  const { deleteSubmission, isDeleting } = useDeleteSubmission()
  const { handlePreview, isPreviewFetching } = useGetPriceChangePreview(submissionId)

  const methods = useForm<SendPriceChangeSchemaFormValues>({
    defaultValues: {
      emailNote: DEFAULT_EMAIL_NOTE,
    },
    mode: "onChange",
    resolver: zodResolver(SendPriceChangeSchema),
  })

  const {
    formState: { isValid, isSubmitted },
  } = methods

  const customerIds = submitContext?.priceChangeItems?.map((el) => el.customerId).filter((el) => !isNil(el)) ?? []

  const { recipients, totalAccounts, totalContacts, isFetching } = useGetPriceChangeRecipients({
    customerIds,
  })

  const confirmCancel = async () => {
    await deleteSubmission({
      submissionId,
    })

    onClose()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const handleSend = async (data: SendPriceChangeSchemaFormValues) => {
    const isSuccess = await promoteDraftSubmission({
      submissionId,
      emailNote: data.emailNote,
    })

    if (!isSuccess) {
      onClose()
      return
    }
    onSuccess()
  }

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={confirmCancel} />
      )}
      <Dialog open onClose={handleCancel} fullWidth maxWidth='lg'>
        <>
          <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent>
            <SendDialogTitle
              title='Are you sure you want to submit the price change?'
              totalAccounts={totalAccounts}
              totalContacts={totalContacts}
              isFetching={isFetching}
            />
            <ViewRecipientAccordion recipients={recipients} isFetching={isFetching} />
            <FormProvider {...methods}>
              <EmailTemplateForm />
            </FormProvider>
          </DialogContent>
          <SendDialogActions
            submitLabel='Yes, Submit'
            isValid={isValid && !isPromoting && !isDeleting}
            isSubmitted={isSubmitted}
            isPreviewFetching={isPreviewFetching}
            onCancel={handleCancel}
            onPreview={() => handlePreview(methods.getValues().emailNote)}
            onSubmit={methods.handleSubmit(handleSend)}
          />
        </>
      </Dialog>
    </>
  )
}

export default SendPriceChange
